import React, { useCallback, useEffect, useState } from "react";
import {
  quoteDetails,
  resendQuoteToZoho,
} from "../../../../stores/actions/quote.actions.types";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { BiLoaderAlt } from "react-icons/bi";
import QuoteStatusUpdate from "../../../Modals/quote-status-update";

// Helper: recursively checks if every value in the attribution object is null/undefined
const isAttributionEmpty = (attribution) => {
  return Object.values(attribution).every((value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "object") return isAttributionEmpty(value);
    return false;
  });
};

const DetailsQuote = ({ quoteDetails, resendQuoteToZoho, loading }) => {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [reload, setReload] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [zohoResponse, setZohoResponse] = useState(null);

  const handleResendToZoho = () => {
    if (!id) return;
    setResendLoading(true);
    setZohoResponse(null);
    resendQuoteToZoho({
      id,
      callback: (data) => {
        setResendLoading(false);
        if (data?.success) setZohoResponse(data?.data?.zohoResponse ?? null);
      },
    });
  };

  const fetchQuoteDetails = useCallback(() => {
    if (!id) return;
    quoteDetails({
      id,
      callback: (data) => {
        if (data?.success) setQuote(data?.data?.quote);
      },
    });
  }, [id, quoteDetails, reload]);

  useEffect(() => {
    fetchQuoteDetails();
  }, [fetchQuoteDetails]);

  return (
    <div>
      {loading ? (
        <div className="flex rounded dark:bg-boxdark bg-white items-center justify-center min-h-96">
          <BiLoaderAlt className="animate-spin text-4xl text-primary dark:text-white" />
        </div>
      ) : (
        <div className="mx-auto rounded p-5 text-black dark:text-white dark:bg-boxdark bg-white">
          <h2 className="text-2xl font-bold mb-4">Quote Details</h2>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <span className="font-semibold">Name:</span> {quote?.name}
            </div>
            <div>
              <span className="font-semibold">Email:</span> {quote?.email}
            </div>
            <div>
              <span className="font-semibold">Phone:</span> {quote?.phone}
            </div>
            <div>
              <span className="font-semibold">Status:</span>{" "}
              <span className="capitalize">{quote?.status || "pending"}</span>
            </div>
            {quote?.productUrl && (
              <div className="col-span-2 break-all">
                <span className="font-semibold">Product URL:</span>{" "}
                <a
                  href={quote.productUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {quote.productUrl}
                </a>
              </div>
            )}
            {quote?.company && (
              <div>
                <span className="font-semibold">Company:</span> {quote?.company}
              </div>
            )}
            {quote?.subject && (
              <div>
                <span className="font-semibold">Subject:</span> {quote?.subject}
              </div>
            )}
            <div>
              <span className="font-semibold">Created At:</span>{" "}
              {quote?.createdAt && new Date(quote.createdAt).toLocaleString()}
            </div>
            <div>
              <span className="font-semibold">Updated At:</span>{" "}
              {quote?.updatedAt && new Date(quote.updatedAt).toLocaleString()}
            </div>
          </div>

          {quote?.message && (
            <div className="mb-6">
              <span className="font-semibold">Message:</span>
              <p className="mt-2 p-3 rounded bg-gray-50 dark:bg-gray-800">
                {quote?.message}
              </p>
            </div>
          )}

          {quote?.products && quote?.products?.length > 0 && (
            <>
              <h3 className="text-lg font-semibold mb-3">Quoted Products</h3>
              <div className="max-w-full overflow-x-auto overflow-y-hidden pb-6">
                <table className="w-full table-auto text-sm text-left border dark:border-gray-500 border-gray-300 rounded">
                  <thead className="text-xs dark:bg-gray-800 bg-white uppercase">
                    <tr>
                      <th scope="col" className="px-4 py-3">
                        #
                      </th>
                      <th scope="col" className="px-4 py-3">
                        Product
                      </th>
                      <th scope="col" className="px-4 py-3">
                        Quantity
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote?.products?.map((product, index) => (
                      <tr
                        key={index}
                        className={
                          index % 2 === 0
                            ? "dark:bg-boxdark bg-white"
                            : "dark:bg-gray-800 bg-gray-50"
                        }
                      >
                        <td className="px-4 py-3">{index + 1}</td>
                        <td className="px-4 py-3">{product?.title || product?.name || product?._id}</td>
                        <td className="px-4 py-3">{product?.quantity || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Attribution */}
          <div className="border border-[#eee] dark:border-dark-3 rounded-md p-4 mb-6">
            <h5 className="font-medium text-dark dark:text-white mb-3">
              Attribution Data
            </h5>
            {quote?.attribution && !isAttributionEmpty(quote.attribution) ? (
              Object.entries(quote.attribution).map(([key, value]) => (
                <div key={key} className="mb-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>
                      {key.charAt(0).toUpperCase() + key.slice(1)}:
                    </strong>{" "}
                    {value === null || value === undefined ? (
                      "N/A"
                    ) : typeof value === "object" ? (
                      <span className="block ml-4">
                        {Object.entries(value).map(([subKey, subValue]) => (
                          <span
                            key={subKey}
                            className="block text-sm text-gray-600 dark:text-gray-400"
                          >
                            <strong>
                              {subKey.charAt(0).toUpperCase() + subKey.slice(1)}:
                            </strong>{" "}
                            {subValue !== null && subValue !== undefined
                              ? String(subValue)
                              : "N/A"}
                          </span>
                        ))}
                      </span>
                    ) : (
                      String(value)
                    )}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No attribution data available for this quote.
              </p>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={() => setIsStatusModalOpen(true)}
              className='block w-[170px] rounded-md bg-primary px-1 py-1 text-white'
            >
              Update Status
            </button>

            <button
              onClick={handleResendToZoho}
              disabled={resendLoading}
              title="Re-sends this quote (including attribution) to the Zoho CRM RFQ function"
              className='flex w-[190px] items-center justify-center rounded-md border border-primary px-1 py-1 text-primary disabled:opacity-50'
            >
              {resendLoading ? (
                <BiLoaderAlt className="animate-spin text-xl" />
              ) : (
                "Resend RFQ to Zoho"
              )}
            </button>
          </div>

          {zohoResponse && (
            <div className="mt-4 rounded-md border border-[#eee] dark:border-dark-3 p-4">
              <h5 className="font-medium text-dark dark:text-white mb-2">
                Zoho Response
              </h5>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all text-xs text-gray-600 dark:text-gray-400">
                {JSON.stringify(zohoResponse, null, 2)}
              </pre>
            </div>
          )}

          <QuoteStatusUpdate
            opened={isStatusModalOpen}
            setOpened={setIsStatusModalOpen}
            status={quote?.status || "pending"}
            id={quote?._id}
            onSuccess={() => setReload(!reload)}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state?.app?.visible,
});

export default connect(mapStateToProps, {
  quoteDetails,
  resendQuoteToZoho,
})(DetailsQuote);
